import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import * as React from 'react';

import type { CampaignDetailDto } from '@/api/campaign-client';
import { useCampaignDetail } from '@/api/use-campaigns';
import { CalendarPanel } from '@/routes/admin/campaigns/calendar-panel';
import { CatalogPanel } from '@/routes/admin/campaigns/catalog-panel';
import { ClientsPanel } from '@/routes/admin/campaigns/clients-panel';
import { DangerZonePanel } from '@/routes/admin/campaigns/danger-zone-panel';
import { LifecycleActionsPanel } from '@/routes/admin/campaigns/lifecycle-actions-panel';
import { SuppliersPanel } from '@/routes/admin/campaigns/suppliers-panel';
import { TargetPricesPanel } from '@/routes/admin/campaigns/target-prices-panel';

// =============================================================================
// campaign-detail-screen.tsx — Écran A « Détail campagne » (Admin), route index de la zone campagne.
// =============================================================================
// GET /admin/campaigns/:id (campagne + cycles + scopes + participations) + commandes de périmètre, de planning
// et de transitions d'état. L'autorité (estampillage, validité temporelle, transitions) reste l'API.
//
// Refonte 2026-09-16 (GATE_HUMAN John : « optimisation et rationalisation de la page, sans perte de
// fonctionnalité ») : l'écran n'est plus qu'une MISE EN PAGE ; chaque fonction vit dans un panneau voisin,
// comportement conservé.
//   Colonne gauche  : Catalogue (catalog-panel) · Prix cibles pivotés (target-prices-panel) · Fournisseurs
//                     (suppliers-panel).
//   Colonne droite  : Actions d'état (lifecycle-actions-panel, seulement si une action est disponible) ·
//                     Clients bénéficiaires + diffusion (clients-panel) · Calendrier (calendar-panel) · zone
//                     rouge Clôture définitive / Abandon (danger-zone-panel).
// Le navigateur de cycle, le fil d'Ariane, le titre <h1> et les onglets (dont « Détail ») sont portés par le
// LAYOUT de zone (campaign-zone-layout-route) : aucun chrome de navigation ici (anti-doublon).
//
// Largeur : deux colonnes dès 1280 px (colonne droite 400 px, 480 px dès 1536 px) ; en dessous, une colonne.
// À 1280 px (barre latérale 240 px), la colonne gauche garde assez de place pour le tableau pivoté sans
// défilement horizontal.

export interface CampaignDetailScreenProps {
  readonly campaignId: string;
}

export function CampaignDetailScreen({
  campaignId,
}: CampaignDetailScreenProps): React.JSX.Element {
  const detail = useCampaignDetail(campaignId);

  if (detail.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement de la campagne…
      </p>
    );
  }
  if (detail.isError) {
    return (
      <div className="space-y-4">
        <p role="alert" className="text-destructive">
          Erreur de chargement : {detail.error.message}
        </p>
        <Link
          to="/admin/campaigns"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:underline"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          Retour à la liste
        </Link>
      </div>
    );
  }

  return <CampaignDetailBody campaign={detail.data} />;
}

function CampaignDetailBody({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  // E13 (relevé Gate Human 2026-09-15) : retour visuel de la clôture DÉFINITIVE. La bannière persiste même si
  // l'action disparaît après la mutation.
  const [finalClosureConfirmed, setFinalClosureConfirmed] = React.useState(false);
  const onFinallyClosed = React.useCallback(() => setFinalClosureConfirmed(true), []);

  return (
    <div className="space-y-6">
      {finalClosureConfirmed ? (
        <div
          role="status"
          className="rounded-md border border-success/40 bg-success/10 px-4 py-3 text-sm text-foreground"
        >
          <span className="font-medium">Campagne clôturée définitivement.</span> Le calcul consolidé
          des résultats (benchmark) a été déclenché ; il s’exécute en arrière-plan. Cette action est
          terminale et ne peut être ni annulée ni relancée.
        </div>
      ) : null}
      <ProvenanceBanner campaign={campaign} />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_400px] 2xl:grid-cols-[minmax(0,1fr)_480px]">
        <div className="min-w-0 space-y-6">
          <CatalogPanel campaign={campaign} />
          <TargetPricesPanel campaign={campaign} />
          <SuppliersPanel campaign={campaign} />
        </div>
        <div className="min-w-0 space-y-6">
          <LifecycleActionsPanel campaign={campaign} />
          <ClientsPanel campaign={campaign} />
          <CalendarPanel campaign={campaign} />
          <DangerZonePanel campaign={campaign} onFinallyClosed={onFinallyClosed} />
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Bandeau de provenance (V-CREATE, DBU §2.5 l.1153) — campagne créée par duplication
// -----------------------------------------------------------------------------

function ProvenanceBanner({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element | null {
  // La provenance n'existe QUE pour une campagne dupliquée (clonedFrom* non-null). Sinon : rien.
  if (campaign.clonedFromIntitule === null) {
    return null;
  }
  const sourceDate =
    campaign.clonedFromDate === null ? '' : ` — ${formatDate(campaign.clonedFromDate)}`;
  return (
    <div
      role="note"
      className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
    >
      Campagne créée par duplication de{' '}
      <span className="font-medium text-foreground">{campaign.clonedFromIntitule}</span>
      {sourceDate}.
    </div>
  );
}

/** Formate un horodatage ISO 8601 en DATE FR lisible (jour, fuseau de l'utilisateur) — bandeau de provenance. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' }).format(date);
}
